/**
 * Custom server emoji (packages/protocol emoji.ts) as the gateway sends
 * them: READY's and GUILD_CREATE's `emojis`, then GUILD_EMOJIS_UPDATE with
 * the whole list after each change. A pure reducer (`applyEmoji`) feeds the
 * store, so tests drive it with plain frames. The console's `custom_emoji`
 * flag decides whether they are drawn (`useCustomEmojiOn`); the list is kept
 * either way, so turning the flag back on needs no reload.
 */
import {
  EmojiRoutes,
  type CreateGuildEmojiRequest,
  type GatewayDispatch,
  type GuildEmoji,
  type ModifyGuildEmojiRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { api } from '../lib/api';
import { flagOn, useFlag } from './config';

export interface EmojiState {
  /** guildId -> its emoji, oldest first (Discord's order). */
  byGuild: Record<Snowflake, GuildEmoji[]>;
  /** emojiId -> emoji, every server's. */
  byId: Record<Snowflake, GuildEmoji & { guild_id: Snowflake }>;
}

export const emptyEmoji = (): EmojiState => ({ byGuild: {}, byId: {} });

function withGuild(s: EmojiState, guildId: Snowflake, list: GuildEmoji[]): EmojiState {
  const byGuild = { ...s.byGuild, [guildId]: list };
  const byId: EmojiState['byId'] = {};
  for (const [gid, l] of Object.entries(byGuild))
    for (const e of l) byId[e.id] = { ...e, guild_id: gid };
  return { byGuild, byId };
}

/** Applies one gateway frame (pure). */
export function applyEmoji(s: EmojiState, frame: GatewayDispatch): EmojiState {
  switch (frame.t) {
    case 'READY': {
      let out = emptyEmoji();
      for (const g of frame.d.guilds) out = withGuild(out, g.id, g.emojis ?? []);
      return out;
    }
    case 'GUILD_CREATE':
      return withGuild(s, frame.d.id, frame.d.emojis ?? []);
    case 'GUILD_EMOJIS_UPDATE':
      return withGuild(s, frame.d.guild_id, frame.d.emojis);
    default:
      return s;
  }
}

export const useEmoji = create<EmojiState>(() => emptyEmoji());

/** The gateway glue (store/session.ts). */
export function feedEmoji(frame: GatewayDispatch): void {
  const s = useEmoji.getState();
  const next = applyEmoji(s, frame);
  if (next !== s) useEmoji.setState(next, true);
}

/** Puts a list the API returned in place (after an upload, before the gateway says so). */
export function setGuildEmojis(guildId: Snowflake, list: GuildEmoji[]): void {
  useEmoji.setState(withGuild(useEmoji.getState(), guildId, list), true);
}

/** True while the console lets custom emoji be used and drawn. */
export function customEmojiOn(): boolean {
  return flagOn('custom_emoji');
}

export function useCustomEmojiOn(): boolean {
  return useFlag('custom_emoji');
}

/** A known custom emoji by ID (any server), or null. */
export function customEmoji(id: Snowflake): (GuildEmoji & { guild_id: Snowflake }) | null {
  return useEmoji.getState().byId[id] ?? null;
}

/** Every custom emoji the person can use: their servers', oldest first. */
export function usableCustomEmoji(): GuildEmoji[] {
  if (!customEmojiOn()) return [];
  return Object.values(useEmoji.getState().byGuild).flat();
}

/* ------------------------------------------------------------ the API */

export const emojiApi = {
  list: (guildId: Snowflake) => api.client.get<GuildEmoji[]>(EmojiRoutes.guildEmojis(guildId)),
  create: (guildId: Snowflake, body: CreateGuildEmojiRequest) =>
    api.client.post<GuildEmoji>(EmojiRoutes.guildEmojis(guildId), body),
  rename: (guildId: Snowflake, emojiId: Snowflake, body: ModifyGuildEmojiRequest) =>
    api.client.patch<GuildEmoji>(EmojiRoutes.guildEmoji(guildId, emojiId), body),
  remove: (guildId: Snowflake, emojiId: Snowflake) =>
    api.client.delete<void>(EmojiRoutes.guildEmoji(guildId, emojiId)),
};
